import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import type { CleanupContactInfo } from "./ansprechpartnerCleanupService";
import { FIRMA_INVERSE } from "../data/firmenverbindungen";

export type CleanupStatus = "open" | "bereinigt";

/**
 * Kanonische Haushaltsrollen (Dropdown) — rein STRUKTURELL, daraus werden die
 * Beziehungen abgeleitet. „Anderes Familienmitglied" gibt es bewusst nicht mehr:
 * Unklares wird als Freitext-BEZIEHUNG erfasst, nicht als Rolle.
 */
export type HouseholdRoleKey =
  | "eltern"
  | "ehepartner"
  | "lebenspartner"
  | "ex_partner"
  | "kind"
  | "grosseltern"
  | "sonstige";

export const HOUSEHOLD_ROLES: Array<{ key: HouseholdRoleKey; label: string }> = [
  { key: "ehepartner", label: "Ehepartner" },
  { key: "lebenspartner", label: "Lebenspartner" },
  { key: "ex_partner", label: "Ex-Partner(in)" },
  { key: "eltern", label: "Elternteil" },
  { key: "kind", label: "Kind" },
  { key: "grosseltern", label: "Großeltern" },
  { key: "sonstige", label: "Sonstige" },
];

/**
 * Auswählbare Beziehungs-Labels (Manuell-Dropdown je Paar) — statischer Fallback,
 * die gepflegte Liste kommt aus `HouseholdRelationLabel`. Freitext läuft über die
 * Sonder-Option „Sonstiges (Freitext)" mit BEIDEN Richtungen; „Verwandt" ist
 * bewusst raus (nichtssagend — siehe Bereinigungsliste „Unklare Beziehung").
 */
export const RELATIONSHIP_OPTIONS: string[] = [
  "Ehemann",
  "Ehefrau",
  "Lebenspartner",
  "Lebenspartnerin",
  "Ex-Partner",
  "Ex-Partnerin",
  "Ex-Ehemann",
  "Ex-Ehefrau",
  "Ex-Lebenspartner",
  "Ex-Lebenspartnerin",
  "Vater",
  "Mutter",
  "Sohn",
  "Tochter",
  "Stiefvater",
  "Stiefmutter",
  "Stiefsohn",
  "Stieftochter",
  "Schwiegervater",
  "Schwiegermutter",
  "Schwiegersohn",
  "Schwiegertochter",
  "Bruder",
  "Schwester",
  "Großvater",
  "Großmutter",
  "Enkel",
  "Enkelin",
  "Onkel",
  "Tante",
  "Neffe",
  "Nichte",
  "Cousin",
  "Cousine",
  "Schwager",
  "Schwägerin",
];

/** Eine Zeile der Haushalts-Übersicht (Anschrift + Nachname). */
export interface HouseholdListRow {
  addrKey: string;
  /** Normalisierter Nachname — zweite Dimension der Haushalts-Gruppierung. */
  nameKey: string;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  memberCount: number;
  hasOpen: boolean;
  /** Verantwortlich (BO-Team/FO-User des ersten Mitglieds). */
  verantwortlich: string | null;
  names: string[] | null;
}

/** Ein Haushaltsmitglied im Detail (Kandidat ⋈ live Contact). */
export interface HouseholdMember extends CleanupContactInfo {
  /** `null` bei Anker-Einstieg: Kontakt hat (noch) keine Kandidaten-Zeile. */
  candidateId: number | null;
  contactId: number | null;
  kundennummer: string | null;
  name: string | null;
  firstName: string | null;
  anrede: string | null;
  geburtsdatum: string | null;
  haushaltsposition: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  /** Bereits zugewiesene Rolle (Candidate). */
  householdRole: string | null;
  /** Rolle am Contact (app-weit). */
  contactHouseholdRole: string | null;
  bereinigtAt: string | null;
  gender: string | null;
  statusId: number | null;
  statusLabel: string | null;
  extraStatuses?: string[];
  /** Bestehende Person-Beziehungen (read-only Anzeige). */
  weitereRelations?: HouseholdConnRow[];
  /** Bestehende Firma-Verbindungen (read-only Anzeige). */
  firmaRelations?: HouseholdConnRow[];
  /** Aktuelle Kundenverbindung des Kontakts (Back-Compat: erste). */
  kundenverbindung?: { id: number; name: string } | null;
  /** Alle Kundenverbindungen des Kontakts (M:N). */
  kundenverbindungen?: { id: number; name: string }[];
  /**
   * Gesetzt, wenn der Kontakt (auch) unter einem anderen Haushalts-Schlüssel geführt
   * wird — typisch beim Anker-Einstieg aus den Kandidatenlisten. Speichern übernimmt
   * ihn in DIESEN Haushalt; die UI weist darauf hin.
   */
  otherHouseholdKey?: string | null;
}

/** Eine bestehende Verbindung (Person oder Firma) eines Mitglieds. */
export interface HouseholdConnRow {
  relationId: number;
  otherId: number;
  otherName: string;
  relationType: string | null;
  function: string | null;
  share: string | null;
  isExtern: boolean;
  sameHousehold: boolean;
}

export interface HouseholdRelationRow {
  relationId: number;
  srcId: number;
  destId: number;
  relationType: string | null;
  /** Freitext-Gegenrichtung („was src für dest ist"), sonst null. */
  reverseRelationType?: string | null;
  sameHousehold: boolean;
}

export interface HouseholdDetail {
  addrKey: string;
  nameKey?: string;
  address: { strasse: string | null; plz: string | null; ort: string | null };
  members: HouseholdMember[];
  relations: HouseholdRelationRow[];
}

/**
 * Neu eingetippte Person (wird beim Speichern angelegt) — Spiegel von
 * `HouseholdMemberRef` aus `apps/api/src/haushalte-cleanup/haushalte-cleanup.service.ts`.
 *
 * Bewusst NICHT `ContactRef`: das Backend kennt hier kein `type`, `ContactRef.type`
 * ist aber Pflicht. Das Backend-Interface fuehrt zusaetzlich ein optionales
 * `contactId` — das wird im Frontend an beiden Verwendungsstellen eine Ebene
 * hoeher gesetzt (`contactId` neben `ref`), `ref` also nur fuer wirklich neue
 * Personen befuellt. Deshalb hier ausgelassen.
 */
export interface HouseholdRefInput {
  lastName?: string;
  firstName?: string;
  gender?: string;
  salutationId?: number;
  salutationLabel?: string;
  birthDate?: string;
}

/** Weitere Person↔Person-Beziehung eines Mitglieds. */
export interface HouseholdPersonRelInput {
  contactId?: number | null;
  ref?: HouseholdRefInput | null;
  art?: string | null;
  sameHousehold?: boolean;
  freeRole?: string | null;
  /** Explizites Label: „was die referenzierte Person für das Mitglied ist". */
  label?: string | null;
  /** Freitext-Gegenrichtung: „was das Mitglied für die referenzierte Person ist". */
  reverseLabel?: string | null;
}

/** Verbindung eines Mitglieds zu einer Firma — eine Rolle je Eintrag (wie AP). */
export interface HouseholdFirmaRelInput {
  firmaContactId?: number | null;
  firmaRef?: { lastName?: string; branche?: string; legalFormId?: number; phone?: string; email?: string; web?: string } | null;
  kind?: "gf" | "gesellschafter" | "mensch";
  verantwortlichkeit?: string;
  share?: string;
  istGeschaeftsfuehrer?: boolean;
  isExtern?: boolean;
  externalCompanyId?: number;
  externalCompanyNew?: { name: string; phone?: string; email?: string; web?: string; branche?: string; legalFormId?: number };
}

export interface HouseholdMemberInput {
  /** Bestehende Kandidaten-Zeile (fehlt bei neu hinzugefügten Mitgliedern). */
  candidateId?: number | null;
  /** Verknüpfter Contact (bestehend bzw. per Suche gewählt). */
  contactId?: number | null;
  /** Neu eingetippter Kontakt (wird beim Speichern angelegt) — für Add-Mitglieder. */
  ref?: HouseholdRefInput | null;
  /** Rolle im Haushalt (Label oder Key). */
  householdRole?: string | null;
  /** Freitext-Rolle bei „Sonstige". */
  freeRole?: string | null;
  /** true = kein Haushaltsmitglied. */
  isNotHousehold?: boolean;
  /** Zusatz-Status „Mensch im Unternehmen" setzen. */
  isMenschImUnternehmen?: boolean;
  /** „Mensch im Unternehmen": zugehörige Firma (Pflicht bei Ja) → Status + (versteckte) Verbindung. */
  menschImUnternehmenFirma?: {
    firmaContactId?: number | null;
    firmaRef?: { lastName?: string; branche?: string; legalFormId?: number; phone?: string; email?: string; web?: string } | null;
  } | null;
  /** Weitere Beziehungen zu Personen. */
  personRelations?: HouseholdPersonRelInput[];
  /** Verbindungen zu Firmen. */
  firmaRelations?: HouseholdFirmaRelInput[];
  /** Nicht-Mitglied: Adresse ändern → Wohnsitz (Default) + optionale separate Postanschrift. */
  addressChange?: {
    wohnsitz: { street?: string | null; plz?: string | null; ort?: string | null; ortsteil?: string | null } | null;
    postanschrift?: { street?: string | null; plz?: string | null; ort?: string | null; ortsteil?: string | null } | null;
  } | null;
  /** Gleiche Anschrift, eigener Haushalt → Wohneinheit-Notiz. */
  wohneinheit?: string | null;
  /** „Gehört zu einem anderen Haushalt": dort als Kandidat eintragen. */
  otherHousehold?: {
    addrKey: string;
    name?: string | null;
    strasse?: string | null;
    plz?: string | null;
    ort?: string | null;
  } | null;
}

/**
 * Kundenverbindungs-Zuordnung für den Haushalt (M:N): mehrere bestehende KVs
 * (groupIds) und/oder mehrere neue (newNames). Zugeordnet werden Haushalt +
 * direkt verwandte Personen.
 */
export interface HouseholdKundenverbindungenInput {
  groupIds?: number[];
  newNames?: string[];
}

/** Eine paarweise Beziehung (Mitglieds-INDIZES in den members-Array des Payloads). */
export interface HouseholdRelationInput {
  aIdx: number;
  bIdx: number;
  /** Label aus Sicht von a: „was b für a ist" (z. B. b ist „Ehefrau" von a). */
  label: string;
  /** Freitext-Gegenrichtung: „was a für b ist" (nur bei Freitext-Beziehungen). */
  reverseLabel?: string | null;
}

/** Haushalts-Liste (Adress-Gruppen) + total für den Header. */
export const listHaushalte = async (
  status: CleanupStatus,
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: HouseholdListRow[]; total: number }> => {
  const { data, total } = await apiFetch<HouseholdListRow[]>("haushalte-cleanup", {
    params: { status, ...(teamId ? { teamId } : {}), ...(q ? { q } : {}), limit, offset },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/**
 * Kandidaten-Quelle der Bereinigung. „addr" ist die klassische Anschriften-Liste;
 * die beiden anderen decken Altbestand ab, der über `HouseholdCandidate` nie sichtbar
 * wurde. Beide neuen Quellen sind selbstklärend (kein Offen/Bereinigt-Status).
 */
export type CandidateSource = "addr" | "relation" | "name";

/** Ein Kontakt in den Kandidatenlisten (⋈ Adresse/Kommunikation vom Backend). */
export interface CandidatePerson extends Partial<CleanupContactInfo> {
  contactId: number;
  lastname: string | null;
  firstName: string | null;
  kundennummer: string | null;
  birthDate: string | null;
  householdRole: string | null;
  gender?: string | null;
  statusLabel?: string | null;
  /** Alle Haushalts-Schlüssel des Kontakts (`null` = noch keinem Haushalt zugeordnet). */
  addrKeys?: string | null;
}

/** Eine Zeile „unklare Beziehung": ein Paar mit zu grobem Beziehungs-Label. */
export interface RelationCandidateRow {
  relationId: number;
  relationType: string | null;
  sameHousehold: boolean;
  bereinigtAt: string | null;
  verantwortlich: string | null;
  a: CandidatePerson;
  b: CandidatePerson;
}

/** Eine Zeile „namensgleich, getrennt": alle Personen eines Nachnamens. */
export interface NameCandidateRow {
  nameKey: string;
  lastname: string;
  memberCount: number;
  /** Zwischen Mitgliedern existiert bereits eine „weitere Beziehung". */
  hasRelation: boolean;
  verantwortlich: string | null;
  members: CandidatePerson[];
  relations: HouseholdRelationRow[];
}

/** Kandidaten „unklare Beziehung" (Label „Verwandt" bzw. außerhalb des Katalogs). */
export const listRelationCandidates = async (
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: RelationCandidateRow[]; total: number }> => {
  const { data, total } = await apiFetch<RelationCandidateRow[]>(
    "haushalte-cleanup/relation-candidates",
    { params: { ...(teamId ? { teamId } : {}), ...(q ? { q } : {}), limit, offset }, signal },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Eine Karte der Worklist „unklare Beziehung", je Haushalt gebündelt. */
export interface RelationHouseholdRow {
  /** Interner Haushalts-Schlüssel (addrKey oder `contact:<id>`) — nur als React-key. */
  hkey: string;
  addrKey: string | null;
  nameKey: string | null;
  /** Nachnamen der beteiligten Personen, z. B. „Mustermann" oder „Beispiel / Muster". */
  label: string;
  /** Anzahl unklarer Beziehungen, die diesem Haushalt zugewiesen sind. */
  count: number;
  /** Echter Adress-Haushalt (HouseholdCandidate) vs. Einzelperson. */
  isAddressHousehold: boolean;
  verantwortlich: string | null;
  /** Anker-Kontakt + weitere Beteiligte → Öffnen über den Anker-Detaileinstieg. */
  anchorContactId: number;
  withContactIds: number[];
}

/** Worklist „unklare Beziehung" — je Haushalt gebündelt statt je Beziehungspaar. */
export const listRelationHouseholds = async (
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: RelationHouseholdRow[]; total: number }> => {
  const { data, total } = await apiFetch<RelationHouseholdRow[]>(
    "haushalte-cleanup/relation-households",
    { params: { ...(teamId ? { teamId } : {}), ...(q ? { q } : {}), limit, offset }, signal },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Eine Karte der Worklist „unklare Beziehung", je Cluster gebündelt. */
export interface RelationClusterRow {
  /** Kleinste beteiligte contactId — stabiler React-Key. */
  clusterId: number;
  /** Nachnamen der beteiligten Haushalte, z. B. „Beispiel / Muster". */
  label: string;
  /** Anzahl verbundener Haushalte im Cluster. */
  householdCount: number;
  /** Anzahl unklarer Beziehungen im Cluster. */
  unclearCount: number;
  verantwortlich: string | null;
  /** Alle beteiligten Personen → Einstieg ins Cluster-Detail. */
  contactIds: number[];
}

/** Ein Haushalt im Cluster-Detail. */
export interface ClusterHousehold {
  hkey: string;
  addrKey: string;
  nameKey: string;
  address: { strasse: string | null; plz: string | null; ort: string | null };
  members: HouseholdMember[];
}

/** Cluster-Detail: Haushalte + alle Beziehungen zwischen deren Mitgliedern. */
export interface RelationClusterDetail {
  households: ClusterHousehold[];
  relations: HouseholdRelationRow[];
}

/** Worklist „unklare Beziehung" — je Cluster (transitive Hülle über Haushalte). */
export const listRelationClusters = async (
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: RelationClusterRow[]; total: number }> => {
  const { data, total } = await apiFetch<RelationClusterRow[]>(
    "haushalte-cleanup/relation-clusters",
    { params: { ...(teamId ? { teamId } : {}), ...(q ? { q } : {}), limit, offset }, signal },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Cluster-Detail über die beteiligten Kontakt-IDs. */
export const fetchRelationCluster = async (
  contactIds: number[],
): Promise<RelationClusterDetail> => {
  const { data } = await apiFetch<RelationClusterDetail>("haushalte-cleanup/relation-cluster", {
    params: { contactIds: contactIds.join(",") },
  });
  return data ?? { households: [], relations: [] };
};

/** Kontakt als echtes Mitglied zu einem Adress-Haushalt hinzufügen. */
export const addClusterMember = async (
  addrKey: string,
  contactId: number,
): Promise<void> => {
  const res = await fetch(apiUrl("haushalte-cleanup/add-member"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ addrKey, contactId }),
  });
  if (!res.ok) {
    let msg = `Hinzufügen fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
};

/** Eine Beziehung schärfen/anlegen (Cluster-Bereinigung). */
export const upsertClusterRelation = async (
  srcId: number,
  destId: number,
  label: string,
  reverseLabel?: string | null,
): Promise<void> => {
  const res = await fetch(apiUrl("haushalte-cleanup/relation"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ srcId, destId, label, reverseLabel: reverseLabel ?? null }),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
};

/** Eine im Cluster-Editor gesammelte, noch nicht gespeicherte Beziehung. */
export interface ClusterRelationInput {
  srcId: number;
  destId: number;
  label: string;
  reverseLabel?: string | null;
}

/**
 * Mehrere gesammelte Beziehungen auf einmal speichern. Der Cluster-Editor legt
 * Änderungen erst auf „Speichern" ab — ein Aufruf statt N.
 */
export const upsertClusterRelations = async (
  items: ClusterRelationInput[],
): Promise<{ saved: number }> => {
  if (!items.length) return { saved: 0 };
  const res = await fetch(apiUrl("haushalte-cleanup/relations"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
};

/** Kandidaten „namensgleich, aber getrennte Haushalte". */
export const listNameCandidates = async (
  onlyWithRelation: boolean,
  teamId: string | undefined,
  q: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: NameCandidateRow[]; total: number }> => {
  const { data, total } = await apiFetch<NameCandidateRow[]>("haushalte-cleanup/name-candidates", {
    params: {
      withRelation: onlyWithRelation ? "true" : "false",
      ...(teamId ? { teamId } : {}),
      ...(q ? { q } : {}),
      limit,
      offset,
    },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Worklist-Typ für die Ausblend-Markierung „keine Bereinigung nötig". */
export type DismissKind = "name" | "relation";

/** Eine ausgeblendete Gruppe („keine Bereinigung nötig"). */
export interface DismissedRow {
  /** Nachname (name) bzw. Haushalts-Schlüssel (relation). */
  refKey: string;
  /** Anzeige-Bezeichnung zum Zeitpunkt des Ausblendens. */
  label: string | null;
  dismissedAt: string;
}

/** „Ausgeblendete"-Ansicht einer Worklist (name/relation). */
export const listDismissedCandidates = async (
  kind: DismissKind,
  signal?: AbortSignal,
): Promise<{ rows: DismissedRow[]; total: number }> => {
  const { data, total } = await apiFetch<DismissedRow[]>("haushalte-cleanup/dismissed", {
    params: { kind },
    signal,
  });
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

/** Kandidat als „keine Bereinigung nötig" ausblenden. */
export const dismissCandidate = async (
  kind: DismissKind,
  refKey: string,
  label: string,
): Promise<void> => {
  const res = await fetch(apiUrl("haushalte-cleanup/dismiss"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ kind, refKey, label }),
  });
  if (!res.ok) throw new Error(`Ausblenden fehlgeschlagen: HTTP ${res.status}`);
};

/** Ausblendung zurücknehmen — Kandidat wieder in die Worklist aufnehmen. */
export const restoreCandidate = async (kind: DismissKind, refKey: string): Promise<void> => {
  const res = await fetch(apiUrl("haushalte-cleanup/dismiss", { kind, refKey }), {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Wiederaufnehmen fehlgeschlagen: HTTP ${res.status}`);
};

/** BO-Teams für den „Verantwortlich"-Filter. */
export const listHaushalteTeams = async (): Promise<Array<{ id: string; name: string | null }>> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>("haushalte-cleanup/teams");
  return data ?? [];
};

/** Haushalts-Detail per Anschrift + Nachname. */
export const fetchHaushalt = async (addrKey: string, nameKey: string): Promise<HouseholdDetail> => {
  const { data } = await apiFetch<HouseholdDetail>("haushalte-cleanup/detail", {
    params: { addrKey, nameKey },
  });
  return data;
};

/**
 * Haushalts-Detail über einen Anker-Kontakt — der Einstieg aus den beiden neuen
 * Kandidatenlisten, deren Kontakte nicht zwingend eine Kandidaten-Zeile besitzen.
 * `withIds` (weitere Beteiligte) erscheinen garantiert als Mitglieder; das Backend
 * bevorzugt beim Schlüssel jede bereits vorhandene Haushalts-Zuordnung.
 */
export const fetchHaushaltByContact = async (
  contactId: number,
  withIds: number[] = [],
): Promise<HouseholdDetail> => {
  const { data } = await apiFetch<HouseholdDetail>("haushalte-cleanup/detail", {
    params: { contactId, ...(withIds.length ? { with: withIds.join(",") } : {}) },
  });
  return data;
};

/** Haushalt speichern: Rollen + Beziehungen + Verbindungen + Kundenverbindung. */
export const saveHaushalt = async (
  addrKey: string,
  members: HouseholdMemberInput[],
  relations: HouseholdRelationInput[],
  kundenverbindungen?: HouseholdKundenverbindungenInput,
): Promise<{ membersProcessed: number; relations: number }> => {
  const res = await fetch(apiUrl("haushalte-cleanup/save"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ addrKey, members, relations, kundenverbindungen }),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json();
};

/** 'm' | 'f' | null aus gender (Männlich/Weiblich) bzw. Anrede (Herr/Frau). */
export function genderKey(gender?: string | null, anrede?: string | null): "m" | "f" | null {
  const g = String(gender ?? "").trim().toLowerCase();
  const s = String(anrede ?? "").trim().toLowerCase();
  if (g.startsWith("m") || s === "herr") return "m";
  if (g.startsWith("w") || s === "frau") return "f";
  return null;
}

const byG = (g: "m" | "f" | null, m: string, f: string, n: string): string =>
  g === "m" ? m : g === "f" ? f : n;

/**
 * Geschlechts-Wahl wie {@link byG}, aber `null` (nicht „Verwandt") bei unbekanntem
 * Geschlecht — Komposition überspringt mehrdeutige Fälle, statt zu raten.
 */
const byGN = (g: "m" | "f" | null, m: string, f: string): string | null =>
  g === "m" ? m : g === "f" ? f : null;

/**
 * Label „was ist B aus Sicht von A" — exakt parallel zur Backend-Ableitung
 * (haushalte-cleanup.service.ts). Für die Live-Vorschau der Beziehungs-Tabelle.
 */
export function deriveRelationLabel(
  roleA: HouseholdRoleKey | null,
  roleB: HouseholdRoleKey | null,
  genderB: "m" | "f" | null,
  freeLabelB?: string,
): string {
  if (!roleA || !roleB) return "";
  const A = roleA;
  const B = roleB;
  const partner = () => byG(genderB, "Ehemann", "Ehefrau", "Ehepartner(in)");
  const lebens = () => byG(genderB, "Lebenspartner", "Lebenspartnerin", "Lebenspartner(in)");
  if (A === "ex_partner" || B === "ex_partner") return byG(genderB, "Ex-Partner", "Ex-Partnerin", "Ex-Partner(in)");
  // Rolle „Sonstige" ist strukturell unbekannt → nichts ableiten.
  if (A === "sonstige" || B === "sonstige") return (freeLabelB || "").trim();
  const spouse = new Set<HouseholdRoleKey>(["ehepartner", "eltern", "grosseltern"]);
  if (spouse.has(A) && spouse.has(B) && A === B) return partner();
  if ((A === "ehepartner" && B === "eltern") || (A === "eltern" && B === "ehepartner")) return partner();
  const partnerish = (r: HouseholdRoleKey) => r === "lebenspartner" || r === "ehepartner" || r === "eltern";
  if ((A === "lebenspartner" && partnerish(B)) || (B === "lebenspartner" && partnerish(A))) return lebens();
  if (A === "kind" && B === "kind") return byG(genderB, "Bruder", "Schwester", "Geschwister");
  // Eltern-Ebene (Eltern/Ehepartner/Lebenspartner) ↔ Kind → Sohn/Tochter bzw.
  // Vater/Mutter. So leiten z. B. „Ehepartner + Ehepartner + Kind + Kind" die
  // Eltern-Kind-Beziehungen automatisch ab (nicht nur bei Rolle „Elternteil").
  if (partnerish(A) && B === "kind") return byG(genderB, "Sohn", "Tochter", "Kind");
  if (A === "kind" && partnerish(B)) return byG(genderB, "Vater", "Mutter", "Elternteil");
  if (A === "grosseltern" && B === "kind") return byG(genderB, "Enkel", "Enkelin", "Enkelkind");
  if (A === "kind" && B === "grosseltern") return byG(genderB, "Großvater", "Großmutter", "Großelternteil");
  if (A === "grosseltern" && B === "eltern") return byG(genderB, "Sohn", "Tochter", "Kind");
  if (A === "eltern" && B === "grosseltern") return byG(genderB, "Vater", "Mutter", "Elternteil");
  // Nicht eindeutig → nichts ableiten, statt zu raten (früher: „Verwandt").
  return "";
}

/**
 * Gegenrichtung eines VERWANDTSCHAFTS-Labels — oder `null`, wenn das Label keine
 * bekannte (auflösbare) Verwandtschaft ist. Aus „B ist <label> für A" wird „A ist
 * <reverse> für B", abhängig vom Geschlecht von A (`targetGender` = Geschlecht der
 * Person, FÜR die das invertierte Label gilt = das angezeigte Gegenüber).
 *
 * `null` für Unbekanntes (Firma-Rollen wie „Geschäftsführer", Freitext wie
 * „Patentante") — so kann der Aufrufer das Original-Label unverändert lassen,
 * statt es fälschlich zu „Verwandt" zu machen.
 */
export function reverseKinshipLabel(
  label: string,
  targetGender: "m" | "f" | null,
  opts?: { partnerFallback?: "unisex" | "opposite" },
): string | null {
  const has = (...xs: string[]) => xs.includes(label);
  // Partner-Familien (symmetrisch). Bekanntes Geschlecht gewinnt; bei null entweder
  // unisex (Default) oder Gegengeschlecht des Input-Labels (opposite; neutraler
  // Input → weibliche Form).
  const partner = (m: string, f: string, n: string): string => {
    if (targetGender === "m") return m;
    if (targetGender === "f") return f;
    if (opts?.partnerFallback === "opposite") return label === m ? f : label === f ? m : f;
    return n;
  };
  if (has("Ehemann", "Ehefrau", "Ehepartner(in)")) return partner("Ehemann", "Ehefrau", "Ehepartner(in)");
  if (has("Lebenspartner", "Lebenspartnerin", "Lebenspartner(in)")) return partner("Lebenspartner", "Lebenspartnerin", "Lebenspartner(in)");
  if (has("Ex-Ehemann", "Ex-Ehefrau")) return partner("Ex-Ehemann", "Ex-Ehefrau", "Ex-Ehepartner(in)");
  if (has("Ex-Lebenspartner", "Ex-Lebenspartnerin")) return partner("Ex-Lebenspartner", "Ex-Lebenspartnerin", "Ex-Lebenspartner(in)");
  if (has("Ex-Partner", "Ex-Partnerin", "Ex-Partner(in)")) return partner("Ex-Partner", "Ex-Partnerin", "Ex-Partner(in)");
  if (has("Vater", "Mutter", "Elternteil")) return byG(targetGender, "Sohn", "Tochter", "Kind");
  if (has("Sohn", "Tochter", "Kind")) return byG(targetGender, "Vater", "Mutter", "Elternteil");
  if (has("Stiefvater", "Stiefmutter")) return byG(targetGender, "Stiefsohn", "Stieftochter", "Stiefkind");
  if (has("Stiefsohn", "Stieftochter", "Stiefkind")) return byG(targetGender, "Stiefvater", "Stiefmutter", "Stiefelternteil");
  if (has("Schwiegervater", "Schwiegermutter")) return byG(targetGender, "Schwiegersohn", "Schwiegertochter", "Schwiegerkind");
  if (has("Schwiegersohn", "Schwiegertochter", "Schwiegerkind")) return byG(targetGender, "Schwiegervater", "Schwiegermutter", "Schwiegerelternteil");
  if (has("Bruder", "Schwester", "Geschwister")) return byG(targetGender, "Bruder", "Schwester", "Geschwister");
  // Halbgeschwister (30.09.2026) — Gegenrichtung wieder Halbgeschwister.
  if (has("Halbbruder", "Halbschwester")) return byG(targetGender, "Halbbruder", "Halbschwester", "Halbgeschwister");
  if (has("Großvater", "Großmutter", "Großelternteil")) return byG(targetGender, "Enkel", "Enkelin", "Enkelkind");
  if (has("Enkel", "Enkelin", "Enkelkind")) return byG(targetGender, "Großvater", "Großmutter", "Großelternteil");
  if (has("Onkel", "Tante")) return byG(targetGender, "Neffe", "Nichte", "Verwandt");
  if (has("Neffe", "Nichte")) return byG(targetGender, "Onkel", "Tante", "Verwandt");
  if (has("Cousin", "Cousine")) return byG(targetGender, "Cousin", "Cousine", "Verwandt");
  // Schwager/Schwägerin sind selbst-reziprok (verschwägert): das Gegenstück ist
  // wieder Schwager/Schwägerin, abhängig vom Geschlecht der Zielperson.
  if (has("Schwager", "Schwägerin")) return byG(targetGender, "Schwager", "Schwägerin", "Verwandt");
  return null;
}

/**
 * Inverse-Map für FIRMA↔FIRMA-Verbindungen — geschlechts-UNabhängig (anders als
 * {@link reverseKinshipLabel}, das „Schwester" als Personen-Geschwister behandelt).
 * Konvention: eine gerichtete Zeile src=ELTERN → 'Tochterfirma' → dest=KIND; auf der
 * Kind-Seite zeigt die Inverse 'Mutterfirma'.
 *
 * Die Tabelle selbst steht seit 09/2026 in data/firmenverbindungen.ts — sie war
 * eine von sechs Kopien und die einzige, die auch das Altlabel „Schwesterfirma"
 * führte. `null` = unbekanntes Firma-Label → Anzeige unverändert; genau dorthin
 * fällt seither auch „Beteiligung", weil sie gerichtet ist und ihr Wort neben
 * dem Namen der anderen Firma bereits das Richtige sagt.
 */
export function reverseFirmaLabel(label: string): string | null {
  return FIRMA_INVERSE[(label ?? "").trim()] ?? null;
}

/**
 * Gegenrichtung eines Beziehungs-Labels für die Bereinigungs-Tabelle. Wie
 * {@link reverseKinshipLabel}, aber mit dem Label SELBST als Fallback statt `null`.
 *
 * Früher war der Fallback „Verwandt" — das überschrieb in `setLabelFor` (Speicher-
 * Richtung drehen) ein unbekanntes Label mit einer Notlösung. Das Label unverändert
 * zurückzugeben verliert nichts; Freitext-Beziehungen tragen ihre echte Gegenrichtung
 * ohnehin explizit mit (`reverseRelationType`).
 */
export function reverseRelationLabel(label: string, targetGender: "m" | "f" | null): string {
  return reverseKinshipLabel(label, targetGender) ?? label;
}

/**
 * Art der Brücke zwischen zwei Haushaltsmitgliedern A und N — aus deren
 * Haushaltsrollen abgeleitet. Bestimmt, wie eine „weitere Beziehung" von A
 * („C ist <X> von A") auf N umgerechnet wird. `null` = keine Ableitung an N.
 */
export type KinshipLink = "partner" | "a_parent_of_n" | "a_child_of_n" | "siblings";

const PARTNERISH_ROLES = new Set<HouseholdRoleKey>(["ehepartner", "lebenspartner", "eltern"]);

/**
 * Brücken-Art zwischen Mitglied A und Mitglied N (aus Sicht von A). partnerish =
 * {ehepartner, lebenspartner, eltern}. Ex-Partner → wie Partner behandelt.
 */
export function linkKind(
  roleA: HouseholdRoleKey | null,
  roleN: HouseholdRoleKey | null,
): KinshipLink | null {
  if (!roleA || !roleN) return null;
  const pA = PARTNERISH_ROLES.has(roleA);
  const pN = PARTNERISH_ROLES.has(roleN);
  if ((pA && pN) || roleA === "ex_partner" || roleN === "ex_partner") return "partner";
  if (pA && roleN === "kind") return "a_parent_of_n";
  if (roleA === "kind" && pN) return "a_child_of_n";
  if (roleA === "kind" && roleN === "kind") return "siblings";
  return null;
}

/**
 * Komponiert „C ist <cToA> von A" ∘ (A↔N-Brücke `link`) → „C ist <Ergebnis> von N",
 * mit dem Geschlecht von C. Nur eindeutige Fälle (Tabelle); mehrdeutige/unbekannte
 * → `null` (überspringen). Gate: nur familiäre Labels (`reverseKinshipLabel != null`).
 */
export function composeKinship(
  cToA: string,
  link: KinshipLink | null,
  genderC: "m" | "f" | null,
): string | null {
  if (!link) return null;
  // Gate: nur familiäre (auflösbare) Verwandtschafts-Labels werden vererbt.
  if (reverseKinshipLabel(cToA, null) == null) return null;
  const has = (...xs: string[]) => xs.includes(cToA);
  switch (link) {
    case "partner": // Verschwägerung
      if (has("Bruder", "Schwester", "Geschwister")) return byGN(genderC, "Schwager", "Schwägerin");
      if (has("Neffe", "Nichte")) return byGN(genderC, "Neffe", "Nichte");
      if (has("Onkel", "Tante")) return byGN(genderC, "Onkel", "Tante");
      if (has("Cousin", "Cousine")) return byGN(genderC, "Cousin", "Cousine");
      return null;
    case "a_parent_of_n":
      if (has("Bruder", "Schwester", "Geschwister")) return byGN(genderC, "Onkel", "Tante");
      if (has("Vater", "Mutter", "Elternteil")) return byGN(genderC, "Großvater", "Großmutter");
      if (has("Sohn", "Tochter", "Kind")) return byGN(genderC, "Bruder", "Schwester");
      if (has("Neffe", "Nichte")) return byGN(genderC, "Cousin", "Cousine");
      return null;
    case "a_child_of_n":
      if (has("Sohn", "Tochter", "Kind")) return byGN(genderC, "Enkel", "Enkelin");
      return null;
    case "siblings": // volle Geschwister angenommen
      if (has("Vater", "Mutter", "Elternteil")) return byGN(genderC, "Vater", "Mutter");
      if (has("Sohn", "Tochter", "Kind")) return byGN(genderC, "Neffe", "Nichte");
      if (has("Onkel", "Tante")) return byGN(genderC, "Onkel", "Tante");
      if (has("Großvater", "Großmutter", "Großelternteil")) return byGN(genderC, "Großvater", "Großmutter");
      if (has("Bruder", "Schwester", "Geschwister")) return byGN(genderC, "Bruder", "Schwester");
      if (has("Neffe", "Nichte")) return byGN(genderC, "Neffe", "Nichte");
      return null;
    default:
      return null;
  }
}

/**
 * Aus einem manuell gesetzten Beziehungs-Label die Haushaltsrollen ableiten:
 * `self` = Rolle der Person, aus deren Sicht das Label gilt; `other` = Rolle der
 * referenzierten Person. Label = „was other für self ist". Für unbekannte/leere
 * Labels null. Wird beim manuellen Beziehungs-Setzen genutzt, um leere Rollen zu füllen.
 */
export function rolesFromRelationLabel(
  label: string,
): { self: HouseholdRoleKey | null; other: HouseholdRoleKey | null } {
  const has = (...xs: string[]) => xs.includes(label);
  if (has("Ehemann", "Ehefrau", "Ehepartner(in)")) return { self: "ehepartner", other: "ehepartner" };
  if (has("Lebenspartner", "Lebenspartnerin", "Lebenspartner(in)")) return { self: "lebenspartner", other: "lebenspartner" };
  if (has("Ex-Partner", "Ex-Partnerin", "Ex-Partner(in)")) return { self: "ex_partner", other: "ex_partner" };
  if (has("Vater", "Mutter", "Elternteil")) return { self: "kind", other: "eltern" }; // other ist Elternteil von self
  if (has("Sohn", "Tochter", "Kind")) return { self: "eltern", other: "kind" }; // other ist Kind von self
  if (has("Großvater", "Großmutter", "Großelternteil")) return { self: "kind", other: "grosseltern" };
  if (has("Enkel", "Enkelin", "Enkelkind")) return { self: "grosseltern", other: "kind" };
  if (has("Bruder", "Schwester", "Geschwister")) return { self: "kind", other: "kind" };
  // Onkel/Tante/Neffe/Nichte/Cousin/Schwager/Schwieger-/Stief-… ergeben KEINE
  // Haushaltsrolle mehr (die Sammelrolle „Anderes Familienmitglied" ist entfallen).
  // Die Rolle bleibt leer und wird bei Bedarf von Hand gesetzt.
  return { self: null, other: null };
}

/** Rollen-Label → Key (für vorbelegte Werte vom Server). */
export function roleKeyFromLabel(v: string | null | undefined): HouseholdRoleKey | null {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return null;
  if (s.startsWith("eltern")) return "eltern";
  if (s.startsWith("ehe")) return "ehepartner";
  if (s.startsWith("lebens")) return "lebenspartner";
  if (s.startsWith("ex")) return "ex_partner";
  if (s === "kind") return "kind";
  if (s.startsWith("groß") || s.startsWith("gross")) return "grosseltern";
  if (s.startsWith("sonst")) return "sonstige";
  return null;
}
